import { useEffect, useState } from 'react';
import { fetchLint, type LintReport } from '../api/knowledge';
import './LintPage.css';

export function LintPage() {
  const [report, setReport] = useState<LintReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    fetchLint()
      .then(setReport)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const totalIssues = report
    ? report.contradictions.length +
      report.orphans.length +
      report.stuck_stubs.length +
      report.stale.length +
      report.broken_sources.length
    : 0;

  return (
    <div className="lint-page">
      <header className="lint-page__header">
        <div>
          <div className="lint-page__title">Health</div>
          <div className="lint-page__subtitle">
            {report
              ? `${report.pages_scanned} pages scanned · ${totalIssues} issue${totalIssues === 1 ? '' : 's'}`
              : '—'}
          </div>
        </div>
        <button type="button" className="lint-page__refresh" onClick={load} disabled={loading}>
          {loading ? 'Scanning...' : 'Run lint'}
        </button>
      </header>

      {error && <div className="lint-page__error">{error}</div>}
      {loading && !report && <div className="lint-page__status">Scanning...</div>}

      {report && (
        <div className="lint-page__sections">
          <LintSection
            title="Contradictions"
            empty={report.contradictions.length === 0}
            tone="warn"
          >
            {report.contradictions.map((c, i) => (
              <Row
                key={`${c.page_a_id}-${c.page_b_id}-${i}`}
                primary={
                  <>
                    <strong>{c.page_a_title}</strong> contradicts{' '}
                    <strong>{c.page_b_title}</strong>
                  </>
                }
                meta={`confidence ${(c.confidence ?? 0).toFixed(2)}`}
              />
            ))}
          </LintSection>

          <LintSection
            title="Orphans"
            empty={report.orphans.length === 0}
            tone="info"
          >
            {report.orphans.map((o) => (
              <Row
                key={o.page_id}
                primary={<strong>{o.title}</strong>}
                meta={o.page_id}
              />
            ))}
          </LintSection>

          <LintSection
            title="Stuck stubs"
            empty={report.stuck_stubs.length === 0}
            tone="warn"
          >
            {report.stuck_stubs.map((s) => (
              <Row
                key={s.page_id}
                primary={<strong>{s.title}</strong>}
                meta={`quality ${s.quality.toFixed(2)} · stuck ${s.days_stuck}d`}
              />
            ))}
          </LintSection>

          <LintSection
            title="Stale"
            empty={report.stale.length === 0}
            tone="info"
          >
            {report.stale.map((s) => (
              <Row
                key={s.page_id}
                primary={<strong>{s.title}</strong>}
                meta={`${s.days_since_update}d since update`}
              />
            ))}
          </LintSection>

          <LintSection
            title="Broken sources"
            empty={report.broken_sources.length === 0}
            tone="warn"
          >
            {report.broken_sources.map((b, i) => (
              <Row
                key={`${b.page_id}-${i}`}
                primary={
                  <>
                    <strong>{b.page_title}</strong> — source "{b.source_title}"
                  </>
                }
                meta={b.source_ref}
              />
            ))}
          </LintSection>
        </div>
      )}
    </div>
  );
}

interface SectionProps {
  title: string;
  empty: boolean;
  tone: 'warn' | 'info';
  children: React.ReactNode;
}

function LintSection({ title, empty, tone, children }: SectionProps) {
  return (
    <section className={`lint-section${empty ? ' lint-section--empty' : ''} lint-section--${tone}`}>
      <header className="lint-section__header">
        <span className="lint-section__title">{title}</span>
        <span className="lint-section__count">{empty ? 'None' : ''}</span>
      </header>
      {!empty && <div className="lint-section__list">{children}</div>}
    </section>
  );
}

interface RowProps {
  primary: React.ReactNode;
  meta?: string;
}

function Row({ primary, meta }: RowProps) {
  return (
    <div className="lint-row">
      <div className="lint-row__primary">{primary}</div>
      {meta && <div className="lint-row__meta">{meta}</div>}
    </div>
  );
}
